import React, { useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { logAudit } from '../../lib/audit';

const CREATE_NEW = '__create_new_product__';

const money = (value) => Number(value || 0).toLocaleString('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0,
});

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ');

const PACKAGING_MARKERS = new Set(['cj', 'cja', 'pq', 'paq', 'bto', 'bt', 'dp']);
const TOKEN_ALIASES = {
  crztal: 'cristal',
  cont: 'contenedor',
  divis: 'division',
  un: 'unidad',
  und: 'unidad',
};

const coreTokenList = (value) => {
  const rawTokens = normalizeText(value).split(' ').filter(Boolean);
  const result = [];
  for (const rawToken of rawTokens) {
    if (PACKAGING_MARKERS.has(rawToken) || /^(cj|cja|pq|paq|bto|bt|dp)\d/.test(rawToken)) break;
    if (/^\d+und$/.test(rawToken) || /^x\d+$/.test(rawToken)) continue;
    if (rawToken === 'und' || rawToken === 'un' || rawToken === 'x') continue;
    result.push(TOKEN_ALIASES[rawToken] || rawToken);
  }
  return result;
};

const tokens = (value) => new Set(coreTokenList(value));

const similarity = (left, right) => {
  const a = normalizeText(left);
  const b = normalizeText(right);
  if (!a || !b) return 0;
  if (a === b) return 1;
  const aTokens = tokens(a);
  const bTokens = tokens(b);
  if (!aTokens.size || !bTokens.size) return 0;
  const intersection = [...aTokens].filter((token) => bTokens.has(token)).length;
  const union = new Set([...aTokens, ...bTokens]).size || 1;
  const jaccard = intersection / union;
  const containment = intersection / Math.max(1, Math.min(aTokens.size, bTokens.size));
  const coreA = [...aTokens].join(' ');
  const coreB = [...bTokens].join(' ');
  const coreExact = coreA === coreB;
  if (coreExact) return 0.99;
  const coverage = intersection / Math.max(aTokens.size, bTokens.size);
  return (jaccard * 0.45) + (containment * 0.35) + (coverage * 0.2);
};

const allowedProduct = (product) => {
  const type = String(product?.type || 'final').toLowerCase();
  return (type === 'insumo' || type === 'final')
    && type !== 'receta'
    && !product?.config?.isIceCream
    && !product?.excludeFromEntries;
};

const rankProducts = (sourceName, catalog) => (
  catalog
    .map((product) => ({ product, score: similarity(sourceName, product.name) }))
    .sort((a, b) => b.score - a.score)
);

const autoMatchProduct = (sourceName, catalog) => {
  const ranked = rankProducts(sourceName, catalog);
  const best = ranked[0];
  const second = ranked[1];
  if (!best) return CREATE_NEW;
  const exact = normalizeText(sourceName) === normalizeText(best.product.name);
  const commonTokens = [...tokens(sourceName)].filter((token) => tokens(best.product.name).has(token)).length;
  const confident = best.score >= 0.7
    && commonTokens >= 2
    && best.score - (second?.score || 0) >= 0.05;
  return exact || confident ? best.product.id : CREATE_NEW;
};

const shortHash = (value) => {
  let hash = 2166136261;
  for (const character of String(value || '')) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const safeProductId = (sourceName) => `import_product_${shortHash(normalizeText(sourceName))}`;

const validateBatch = (raw) => {
  if (!raw || !Array.isArray(raw.entries) || !raw.entries.length) {
    throw new Error('El archivo debe contener una lista "entries" con al menos una entrada.');
  }

  const seen = new Set();
  const entries = raw.entries.map((entry, entryIndex) => {
    const invoiceNumber = String(entry.invoiceNumber || '').trim();
    const registrationDate = String(entry.registrationDate || '').trim();
    if (!invoiceNumber) throw new Error(`La entrada ${entryIndex + 1} no tiene numero de factura.`);
    if (seen.has(invoiceNumber.toLowerCase())) throw new Error(`La factura ${invoiceNumber} esta repetida en el archivo.`);
    seen.add(invoiceNumber.toLowerCase());
    if (!/^\d{4}-\d{2}-\d{2}$/.test(registrationDate)) {
      throw new Error(`La factura ${invoiceNumber} no tiene una fecha valida (YYYY-MM-DD).`);
    }
    if (!Array.isArray(entry.products) || !entry.products.length) {
      throw new Error(`La factura ${invoiceNumber} no tiene productos.`);
    }

    const products = entry.products.map((product, productIndex) => {
      const sourceName = String(product.sourceName || product.productName || '').trim();
      const quantity = Number(product.quantity);
      const unitCost = Number(product.unitCost);
      const totalCost = Number(product.totalCost);
      const stockMultiplier = Number(product.stockMultiplier ?? 1);
      if (!sourceName || !Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(unitCost) || unitCost < 0) {
        throw new Error(`Producto invalido en ${invoiceNumber}, renglon ${productIndex + 1}.`);
      }
      if (!Number.isFinite(stockMultiplier) || stockMultiplier <= 0) {
        throw new Error(`El multiplicador de inventario de "${sourceName}" no es valido.`);
      }
      const calculated = Math.round(quantity * unitCost);
      if (!Number.isFinite(totalCost) || Math.abs(calculated - totalCost) > 1) {
        throw new Error(`El total de "${sourceName}" no coincide con cantidad por costo unitario.`);
      }
      return { sourceName, quantity, unitCost, totalCost: Math.round(totalCost), stockMultiplier };
    });

    const calculatedTotal = products.reduce((sum, product) => sum + product.totalCost, 0);
    const expectedTotal = Number(entry.expectedTotal ?? calculatedTotal);
    if (!Number.isFinite(expectedTotal) || Math.abs(calculatedTotal - expectedTotal) > 1) {
      throw new Error(`El total de la factura ${invoiceNumber} no coincide: ${money(calculatedTotal)} vs ${money(expectedTotal)}.`);
    }

    return {
      invoiceNumber,
      registrationDate,
      expectedTotal: Math.round(expectedTotal),
      products,
    };
  });

  return {
    batchId: String(raw.batchId || `lote-${Date.now()}`).replace(/[^a-zA-Z0-9_-]+/g, '-'),
    productAliases: raw.productAliases && typeof raw.productAliases === 'object'
      ? raw.productAliases
      : {},
    entries,
  };
};

const safeEntryId = (batchId, invoiceNumber) => (
  `import_${batchId}_${invoiceNumber}`.replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 300)
);

const IncomingBatchImport = ({ currentUser, products, onClose, onImported }) => {
  const inputRef = useRef(null);
  const [batch, setBatch] = useState(null);
  const [mappings, setMappings] = useState({});
  const [duplicates, setDuplicates] = useState(new Set());
  const [loadingFile, setLoadingFile] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  const catalog = useMemo(
    () => (products || []).filter(allowedProduct).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es')),
    [products],
  );
  const catalogById = useMemo(() => new Map(catalog.map((product) => [product.id, product])), [catalog]);
  const importableEntries = (batch?.entries || []).filter((entry) => !duplicates.has(entry.invoiceNumber));
  const namesToImport = new Set();
  importableEntries.forEach((entry) => entry.products.forEach((product) => namesToImport.add(product.sourceName)));
  const uniqueNames = [...namesToImport].sort((a, b) => a.localeCompare(b, 'es'));
  const suggestionsByName = new Map(uniqueNames.map((name) => [name, rankProducts(name, catalog)[0] || null]));
  const unresolved = uniqueNames.filter((name) => (
    mappings[name] !== CREATE_NEW && !catalogById.has(mappings[name])
  ));
  const newProductNames = uniqueNames.filter((name) => mappings[name] === CREATE_NEW);
  const existingProductNames = uniqueNames.filter((name) => catalogById.has(mappings[name]));
  const total = importableEntries.reduce((sum, entry) => sum + entry.expectedTotal, 0);

  const downloadDiagnostics = () => {
    const payload = {
      generatedAt: new Date().toISOString(),
      catalogCount: catalog.length,
      batchId: batch?.batchId || '',
      comparisons: uniqueNames.map((sourceName) => ({
        sourceName,
        candidates: rankProducts(sourceName, catalog).slice(0, 10).map(({ product, score }) => ({
          id: product.id,
          name: product.name || '',
          barcode: product.barcode || '',
          category: product.category || '',
          type: product.type || '',
          score: Number(score.toFixed(4)),
        })),
      })),
      catalog: catalog.map((product) => ({
        id: product.id,
        name: product.name || '',
        barcode: product.barcode || '',
        category: product.category || '',
        type: product.type || '',
      })),
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `diagnostico-catalogo-${batch?.batchId || 'entradas'}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const findDuplicates = async (entries) => {
    const found = new Set();
    await Promise.all(entries.map(async (entry) => {
      const snapshot = await getDocs(query(
        collection(db, 'entradas'),
        where('invoiceNumber', '==', entry.invoiceNumber),
      ));
      if (!snapshot.empty) found.add(entry.invoiceNumber);
    }));
    return found;
  };

  const handleAudit = async () => {
    if (!batch || auditing) return;
    setAuditing(true);
    setAuditResult(null);
    setError('');
    try {
      const details = [];
      let totalFound = 0;
      for (const expected of batch.entries) {
        const entryRef = doc(db, 'entradas', safeEntryId(batch.batchId, expected.invoiceNumber));
        const snapshot = await getDoc(entryRef);
        if (!snapshot.exists()) {
          details.push({ invoiceNumber: expected.invoiceNumber, ok: false, message: 'No se encontro la entrada importada.' });
          continue;
        }
        const actual = snapshot.data() || {};
        const actualProducts = Array.isArray(actual.products) ? actual.products : [];
        const actualTotal = actualProducts.reduce((sum, line) => sum + Number(line.totalCost || 0), 0);
        const missingProduct = actualProducts.find((line) => !line.productId || !catalogById.has(line.productId));
        const fieldErrors = [];
        if (actual.invoiceNumber !== expected.invoiceNumber) fieldErrors.push('factura');
        if (actual.registrationDate !== expected.registrationDate) fieldErrors.push('fecha');
        if (String(actual.entryType || '').toLowerCase() !== 'compra') fieldErrors.push('tipo');
        if (String(actual.paymentStatus || '').toLowerCase() !== 'pagado') fieldErrors.push('estado de pago');
        if (actual.affectsCash !== false) fieldErrors.push('afecta caja');
        if (String(actual.supplier || '') !== '') fieldErrors.push('proveedor');
        if (Math.abs(Number(actual.totalEntryCost || 0) - expected.expectedTotal) > 1) fieldErrors.push('total guardado');
        if (Math.abs(actualTotal - expected.expectedTotal) > 1) fieldErrors.push('suma de productos');
        if (actualProducts.length !== expected.products.length) fieldErrors.push('cantidad de renglones');
        if (missingProduct) fieldErrors.push('producto inexistente');

        const ok = fieldErrors.length === 0;
        if (ok) totalFound += Number(actual.totalEntryCost || 0);
        details.push({
          invoiceNumber: expected.invoiceNumber,
          ok,
          message: ok ? `${actualProducts.length} renglones y ${money(actual.totalEntryCost)}` : `Revisar: ${fieldErrors.join(', ')}`,
        });
      }

      const expectedTotal = batch.entries.reduce((sum, entry) => sum + entry.expectedTotal, 0);
      setAuditResult({
        ok: details.every((detail) => detail.ok) && Math.abs(totalFound - expectedTotal) <= 1,
        details,
        totalFound,
        expectedTotal,
      });
    } catch (auditError) {
      setError(auditError?.message || 'No se pudo auditar el lote.');
    } finally {
      setAuditing(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoadingFile(true);
    setError('');
    setResult(null);
    setAuditResult(null);
    try {
      if (!catalog.length) {
        throw new Error('No se pudo cargar el catalogo de productos. Cierra esta ventana, vuelve a abrir Importar lote e intenta nuevamente.');
      }
      const parsed = validateBatch(JSON.parse(await file.text()));
      const names = new Set();
      parsed.entries.forEach((entry) => entry.products.forEach((product) => names.add(product.sourceName)));
      const initialMappings = {};
      names.forEach((name) => {
        const configuredId = String(parsed.productAliases?.[name] || '');
        initialMappings[name] = configuredId && catalogById.has(configuredId)
          ? configuredId
          : autoMatchProduct(name, catalog);
      });
      const existing = await findDuplicates(parsed.entries);
      setBatch(parsed);
      setMappings(initialMappings);
      setDuplicates(existing);
    } catch (fileError) {
      setBatch(null);
      setMappings({});
      setDuplicates(new Set());
      setError(fileError?.message || 'No se pudo leer el archivo.');
    } finally {
      setLoadingFile(false);
      event.target.value = '';
    }
  };

  const handleImport = async () => {
    if (!batch || unresolved.length || !importableEntries.length || importing) return;
    setImporting(true);
    setError('');
    try {
      const existing = await findDuplicates(importableEntries);
      if (existing.size) {
        setDuplicates((previous) => new Set([...previous, ...existing]));
        throw new Error('Se encontraron facturas registradas recientemente. Se excluyeron para evitar duplicados; revisa el lote e intenta de nuevo.');
      }

      const registeredBy = typeof currentUser === 'string'
        ? currentUser
        : (currentUser?.username || currentUser?.email || currentUser?.uid || '');
      const newProductDefinitions = new Map(newProductNames.map((sourceName) => {
        const id = safeProductId(sourceName);
        return [id, {
          id,
          name: sourceName.toUpperCase(),
          barcode: `AUTO-${shortHash(sourceName).toUpperCase()}`,
          image: '',
          price: 0,
          category: 'Icopor',
          type: 'insumo',
          unit: 'unidad',
          config: null,
          excludeFromEntries: false,
          registeredBy,
          registrationDate: new Date().toISOString().slice(0, 10),
          createdByImport: true,
          importSourceName: sourceName,
        }];
      }));

      const prepared = importableEntries.map((entry) => ({
        ...entry,
        ref: doc(db, 'entradas', safeEntryId(batch.batchId, entry.invoiceNumber)),
        products: entry.products.map((line, index) => {
          const mapping = mappings[line.sourceName];
          const product = mapping === CREATE_NEW
            ? newProductDefinitions.get(safeProductId(line.sourceName))
            : catalogById.get(mapping);
          if (!product) throw new Error(`Falta relacionar el producto ${line.sourceName}.`);
          const stockQuantity = line.quantity * line.stockMultiplier;
          const stockUnitCost = stockQuantity > 0 ? line.totalCost / stockQuantity : 0;
          return {
            id: `${safeEntryId(batch.batchId, entry.invoiceNumber)}-${index + 1}`,
            productId: product.id,
            productName: product.name,
            barcode: product.barcode || '',
            productUnit: product.unit || 'unidad',
            quantity: stockQuantity,
            unitCost: stockUnitCost,
            totalCost: line.totalCost,
            sourceName: line.sourceName,
            purchaseQuantity: line.quantity,
            purchaseUnitCost: line.unitCost,
            stockMultiplier: line.stockMultiplier,
          };
        }),
      }));

      const inventoryChanges = new Map();
      prepared.forEach((entry) => entry.products.forEach((line) => {
        const current = inventoryChanges.get(line.productId) || { quantity: 0, cost: 0 };
        current.quantity += line.quantity;
        current.cost += line.totalCost;
        inventoryChanges.set(line.productId, current);
      }));
      const productRefs = [...inventoryChanges.keys()].map((id) => doc(db, 'productos', id));

      await runTransaction(db, async (transaction) => {
        const entrySnapshots = [];
        for (const entry of prepared) entrySnapshots.push(await transaction.get(entry.ref));
        const productSnapshots = [];
        for (const productRef of productRefs) productSnapshots.push(await transaction.get(productRef));

        const repeated = entrySnapshots.find((snapshot) => snapshot.exists());
        if (repeated) throw new Error('Este archivo ya fue importado anteriormente.');
        const missing = productSnapshots.find((snapshot) => (
          !snapshot.exists() && !newProductDefinitions.has(snapshot.id)
        ));
        if (missing) throw new Error('Uno de los productos relacionados ya no existe en el catalogo.');

        prepared.forEach((entry) => {
          transaction.set(entry.ref, {
            invoiceNumber: entry.invoiceNumber,
            supplier: '',
            supplierId: null,
            entryType: 'compra',
            paymentStatus: 'pagado',
            paidFrom: 'otro',
            affectsCash: false,
            turnId: null,
            proofImage: '',
            proofImageId: '',
            products: entry.products,
            registrationDate: entry.registrationDate,
            date: entry.registrationDate,
            dateISO: new Date().toISOString(),
            totalEntryCost: entry.expectedTotal,
            registeredBy,
            importBatchId: batch.batchId,
            importSource: 'archivo-json',
            timestamp: serverTimestamp(),
          });
        });

        productSnapshots.forEach((snapshot) => {
          const change = inventoryChanges.get(snapshot.id);
          const data = snapshot.data() || {};
          const oldStock = Number(data.stock || 0);
          const oldAverage = Number(data.costPrice || 0);
          const newStock = oldStock + change.quantity;
          const newAverage = newStock > 0
            ? ((oldStock * oldAverage) + change.cost) / newStock
            : 0;
          if (snapshot.exists()) {
            transaction.update(snapshot.ref, {
              stock: newStock,
              costPrice: newAverage,
              updatedAt: serverTimestamp(),
            });
          } else {
            const definition = newProductDefinitions.get(snapshot.id);
            const productData = { ...definition };
            delete productData.id;
            transaction.set(snapshot.ref, {
              ...productData,
              stock: change.quantity,
              costPrice: change.quantity > 0 ? change.cost / change.quantity : 0,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          }
        });
      });

      await Promise.allSettled([
        ...prepared.map((entry) => logAudit({
        module: 'Entradas',
        action: 'incoming.created',
        severity: 'info',
        target: { id: entry.ref.id, nombre: entry.invoiceNumber },
        details: {
          invoiceNumber: entry.invoiceNumber,
          totalEntryCost: entry.expectedTotal,
          importBatchId: batch.batchId,
          imported: true,
        },
        }, currentUser)),
        ...[...newProductDefinitions.values()].map((product) => logAudit({
          module: 'Productos',
          action: 'product.created',
          severity: 'info',
          target: { id: product.id, nombre: product.name },
          details: { imported: true, importBatchId: batch.batchId, type: 'insumo', category: 'Icopor' },
        }, currentUser)),
      ]);

      setResult({ count: prepared.length, total, createdProducts: newProductDefinitions.size });
      await onImported?.();
    } catch (importError) {
      const message = importError?.code === 'permission-denied'
        ? 'Tu usuario puede registrar entradas, pero no tiene permiso para crear productos. Un administrador debe habilitar Edicion en Productos o Inventarios.'
        : (importError?.message || 'No se pudo importar el lote.');
      setError(message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-3 py-4 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-32px)] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b px-5 py-4 sm:px-7">
          <div>
            <h2 className="text-xl font-black text-slate-900 sm:text-2xl">Importar entradas por lote</h2>
            <p className="mt-1 text-sm text-slate-500">Valida facturas, totales y productos antes de modificar el inventario. Catalogo consultado: {catalog.length} productos.</p>
          </div>
          <button onClick={onClose} disabled={importing} className="rounded-xl px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-50">Cerrar</button>
        </div>

        <div className="overflow-y-auto px-5 py-5 sm:px-7">
          <input ref={inputRef} type="file" accept="application/json,.json" onChange={handleFile} className="hidden" />
          {!result && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={loadingFile || importing}
              className="w-full rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50 px-5 py-6 text-center font-bold text-emerald-800 transition hover:border-emerald-500 disabled:opacity-60"
            >
              {loadingFile ? 'Revisando archivo...' : batch ? 'Seleccionar otro archivo JSON' : 'Seleccionar archivo JSON del lote'}
            </button>
          )}

          {error && <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</div>}

          {result ? (
            <div className="mt-4 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 text-center">
              <p className="text-xl font-black text-emerald-900">Lote importado correctamente</p>
              <p className="mt-2 text-emerald-800">{result.count} entradas por {money(result.total)}. No afectaron caja.</p>
              {result.createdProducts > 0 && (
                <p className="mt-1 text-sm font-semibold text-emerald-800">Se crearon {result.createdProducts} insumos nuevos en la categoria Icopor.</p>
              )}
              <button onClick={onClose} className="mt-5 rounded-2xl bg-emerald-600 px-5 py-3 font-bold text-white hover:bg-emerald-700">Volver a Entradas</button>
            </div>
          ) : batch && (
            <>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-2xl bg-slate-50 p-4"><p className="text-xs font-bold uppercase text-slate-400">Entradas nuevas</p><p className="mt-1 text-2xl font-black text-slate-900">{importableEntries.length}</p></div>
                <div className="rounded-2xl bg-slate-50 p-4"><p className="text-xs font-bold uppercase text-slate-400">Total</p><p className="mt-1 text-2xl font-black text-slate-900">{money(total)}</p></div>
                <div className="rounded-2xl bg-emerald-50 p-4"><p className="text-xs font-bold uppercase text-slate-400">Ya existentes</p><p className="mt-1 text-2xl font-black text-slate-900">{existingProductNames.length}</p></div>
                <div className={`rounded-2xl p-4 ${newProductNames.length ? 'bg-sky-50' : 'bg-emerald-50'}`}><p className="text-xs font-bold uppercase text-slate-400">Productos por crear</p><p className="mt-1 text-2xl font-black text-slate-900">{newProductNames.length}</p></div>
              </div>

              {duplicates.size > 0 && (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  No se volveran a importar: {[...duplicates].join(', ')}.
                </div>
              )}

              {auditResult && (
                <div className={`mt-4 rounded-2xl border px-4 py-4 ${auditResult.ok ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
                  <p className={`font-black ${auditResult.ok ? 'text-emerald-900' : 'text-rose-900'}`}>
                    {auditResult.ok ? 'Auditoria correcta' : 'La auditoria encontro diferencias'}
                  </p>
                  <p className="mt-1 text-sm text-slate-700">
                    Encontrado: {money(auditResult.totalFound)} / Esperado: {money(auditResult.expectedTotal)}
                  </p>
                  <div className="mt-3 space-y-1 text-sm">
                    {auditResult.details.map((detail) => (
                      <div key={detail.invoiceNumber} className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                        <span className="font-semibold text-slate-800">{detail.ok ? 'OK' : 'ERROR'} · {detail.invoiceNumber}</span>
                        <span className="text-slate-600">{detail.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {newProductNames.length > 0 && (
                <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                  Los productos nuevos quedaran como <b>insumos</b>, en la categoria <b>Icopor</b>, con unidad "unidad" y precio de venta $0. Puedes reclasificarlos despues desde Productos.
                </div>
              )}

              <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
                <div className="flex flex-col gap-3 border-b bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="font-black text-slate-900">Revision de productos</h3>
                    <p className="mt-1 text-xs text-slate-500">Las coincidencias seguras usan el catalogo; los demas se crearan automaticamente. Puedes cambiar cualquier decision.</p>
                  </div>
                  <button
                    type="button"
                    onClick={downloadDiagnostics}
                    className="shrink-0 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100"
                  >
                    Descargar diagnostico
                  </button>
                </div>
                <div className="max-h-[42vh] divide-y overflow-y-auto">
                  {uniqueNames.map((name) => (
                    <div key={name} className="grid gap-2 px-4 py-3 md:grid-cols-[1fr_1fr] md:items-center">
                      <div>
                        <span className="text-sm font-semibold text-slate-700">{name}</span>
                        {mappings[name] === CREATE_NEW && suggestionsByName.get(name)?.score >= 0.35 && (
                          <button
                            type="button"
                            onClick={() => setMappings((previous) => ({ ...previous, [name]: suggestionsByName.get(name).product.id }))}
                            className="mt-1 block text-left text-xs font-semibold text-sky-700 hover:text-sky-900 hover:underline"
                          >
                            Posible coincidencia: {suggestionsByName.get(name).product.name} ({Math.round(suggestionsByName.get(name).score * 100)}%)
                          </button>
                        )}
                      </div>
                      <select
                        value={mappings[name] || ''}
                        onChange={(event) => setMappings((previous) => ({ ...previous, [name]: event.target.value }))}
                        className={`w-full rounded-xl border px-3 py-2 text-sm ${mappings[name] === CREATE_NEW ? 'border-sky-300 bg-sky-50' : mappings[name] ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
                      >
                        <option value="">Seleccionar producto...</option>
                        <option value={CREATE_NEW}>Crear producto nuevo: {name}</option>
                        {catalog.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {!result && batch && (
          <div className="flex flex-col gap-3 border-t bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
            <p className="text-xs text-slate-500">Pagadas · Compra · Sin proveedor · No afecta caja</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              {duplicates.size > 0 && (
                <button
                  onClick={handleAudit}
                  disabled={auditing || importing}
                  className="rounded-2xl border border-indigo-300 bg-indigo-50 px-6 py-3 font-bold text-indigo-800 hover:bg-indigo-100 disabled:opacity-50"
                >
                  {auditing ? 'Auditando...' : `Auditar ${duplicates.size} entradas registradas`}
                </button>
              )}
              {importableEntries.length > 0 && (
                <button
                  onClick={handleImport}
                  disabled={importing || unresolved.length > 0}
                  className="rounded-2xl bg-emerald-600 px-6 py-3 font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {importing
                    ? 'Importando lote...'
                    : `${newProductNames.length ? `Crear ${newProductNames.length} productos e importar` : 'Importar'} ${importableEntries.length} entradas`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncomingBatchImport;
